import DateInput from './DateInput';
// @generated-by docnow-d10-clearings-tab v1
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Card, EmptyState } from './Shared';
import { billingAPI } from '../services/api';

export default function DoctorMonthlyClearingsTab({ doctorId }) {
  const [clearings, setClearings] = useState(null);
  const [error, setError] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [payoutDate, setPayoutDate] = useState('');
  const [payoutReference, setPayoutReference] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const now = new Date();
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [genYear, setGenYear] = useState(prevMonthDate.getFullYear());
  const [genMonth, setGenMonth] = useState(prevMonthDate.getMonth() + 1);
  const [generating, setGenerating] = useState(false);

  const loadClearings = async () => {
    try {
      const res = await billingAPI.listClearings({ doctorId });
      setClearings(res.data?.data || []);
      setError(null);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Αποτυχία φόρτωσης εκκαθαρίσεων';
      setError(msg);
      setClearings([]);
    }
  };

  useEffect(() => {
    if (!doctorId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await billingAPI.listClearings({ doctorId });
        if (cancelled) return;
        setClearings(res.data?.data || []);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.error || 'Αποτυχία φόρτωσης εκκαθαρίσεων';
        setError(msg);
        setClearings([]);
      }
    })();
    return () => { cancelled = true; };
  }, [doctorId]);

  const fmtEuro = (cents) => {
    if (cents == null) return '—';
    const n = Number(cents) / 100;
    return new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(n);
  };

  const MONTHS_EL = ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];

  const fmtPeriod = (year, month) => {
    const m = MONTHS_EL[(Number(month) - 1)] || `Μήνας ${month}`;
    return `${m} ${year}`;
  };

  const fmtDate = (s) => {
    if (!s) return null;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('el-GR');
  };

  const statusBadge = (status) => {
    const config = {
      pending:  { label: 'Σε αναμονή',  bg: '#FEF3C7', fg: '#92400E', border: '#F59E0B' },
      paid:     { label: 'Πληρώθηκε',   bg: '#D1FAE5', fg: '#065F46', border: '#10B981' },
      failed:   { label: 'Απέτυχε',     bg: '#FEE2E2', fg: '#991B1B', border: '#DC2626' },
      on_hold:  { label: 'Σε αναστολή', bg: '#E0E7FF', fg: '#3730A3', border: '#6366F1' },
    };
    const c = config[status] || { label: status, bg: '#F3F4F6', fg: '#374151', border: '#9CA3AF' };
    return (
      <span style={{ padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}>{c.label}</span>
    );
  };

  const openPayForm = (clearing) => {
    setPayingId(clearing.id);
    setPayoutDate(new Date().toISOString().slice(0, 10));
    setPayoutReference('');
  };

  const closePayForm = () => {
    setPayingId(null);
    setPayoutDate('');
    setPayoutReference('');
  };

  const submitMarkPaid = async (clearingId) => {
    const ref = (payoutReference || '').trim();
    if (!payoutDate) { toast.error('Συμπληρώστε ημερομηνία πληρωμής'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(payoutDate)) { toast.error('Η ημερομηνία πρέπει να είναι σε μορφή YYYY-MM-DD'); return; }
    const today = new Date().toISOString().slice(0, 10);
    if (payoutDate > today) { toast.error('Η ημερομηνία πληρωμής δεν μπορεί να είναι στο μέλλον'); return; }
    if (!ref) { toast.error('Συμπληρώστε αριθμό αναφοράς τράπεζας'); return; }

    setSubmitting(true);
    try {
      await billingAPI.markPaid(clearingId, { payoutDate, payoutReference: ref });
      toast.success('✅ Η εκκαθάριση καταχωρήθηκε ως πληρωμένη');
      closePayForm();
      await loadClearings();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === 'INVALID_STATUS_TRANSITION') {
        toast.error(`Δεν είναι δυνατή η αλλαγή κατάστασης: τρέχουσα = ${data.currentStatus}`);
        await loadClearings();
      } else {
        toast.error(data?.error || 'Αποτυχία καταχώρησης πληρωμής');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitGenerate = async () => {
    const y = Number(genYear);
    const m = Number(genMonth);
    if (!Number.isInteger(y) || y < 2024 || y > 2100) { toast.error('Έτος εκτός ορίων (2024–2100)'); return; }
    if (!Number.isInteger(m) || m < 1 || m > 12) { toast.error('Μήνας εκτός ορίων (1–12)'); return; }
    const nowY = now.getFullYear();
    const nowM = now.getMonth() + 1;
    if (y > nowY || (y === nowY && m >= nowM)) { toast.error('Επιτρέπεται μόνο για περασμένους μήνες'); return; }

    setGenerating(true);
    try {
      const res = await billingAPI.generateClearing(doctorId, y, m);
      const summary = res.data?.data?.summary;
      if (summary) {
        toast.success(`✅ Δημιουργήθηκε εκκαθάριση ${fmtPeriod(y, m)}: ${summary.billableCount} χρεώσιμες, ${fmtEuro(summary.doctorShareCents)} στον ιατρό`);
      } else {
        toast.success('✅ Η εκκαθάριση δημιουργήθηκε');
      }
      await loadClearings();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Αποτυχία δημιουργίας εκκαθάρισης');
    } finally {
      setGenerating(false);
    }
  };

  const monthOptions = MONTHS_EL.map((name, idx) => ({ value: idx + 1, label: name }));
  const currentYear = now.getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  if (clearings === null) {
    return (
      <Card title="💸 Μηνιαίες Εκκαθαρίσεις">
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>Φόρτωση...</div>
      </Card>
    );
  }

  if (error && clearings.length === 0) {
    return (
      <Card title="💸 Μηνιαίες Εκκαθαρίσεις">
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--danger, #c00)' }}>{error}</div>
      </Card>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card title="🛠️ Δημιουργία Εκκαθάρισης">
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
            Υπολογίζει την εκκαθάριση για συγκεκριμένο μήνα. Επιτρέπεται μόνο για περασμένους μήνες. Η ενέργεια είναι ασφαλής: αν υπάρχει ήδη εκκαθάριση για αυτόν τον μήνα, θα ενημερωθεί.
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 12 }}>Μήνας</label>
              <select value={genMonth} onChange={e => setGenMonth(Number(e.target.value))} style={{ padding: 10, borderRadius: 8, border: '1px solid var(--border)', minWidth: 160 }}>
                {monthOptions.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 12 }}>Έτος</label>
              <select value={genYear} onChange={e => setGenYear(Number(e.target.value))} style={{ padding: 10, borderRadius: 8, border: '1px solid var(--border)', minWidth: 100 }}>
                {yearOptions.map(y => (<option key={y} value={y}>{y}</option>))}
              </select>
            </div>
            <button onClick={submitGenerate} disabled={generating} style={{ background: 'var(--crimson, #C0185A)', color: 'white', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: generating ? 'wait' : 'pointer', fontWeight: 600, fontSize: 13, opacity: generating ? 0.6 : 1 }}>
              {generating ? 'Δημιουργία...' : '🛠️ Δημιουργία'}
            </button>
          </div>
        </div>
      </Card>

      {clearings.length === 0 ? (
        <Card title="💸 Μηνιαίες Εκκαθαρίσεις">
          <EmptyState icon="💸" title="Καμία εκκαθάριση" subtitle="Ο ιατρός δεν έχει εκκαθαρίσεις. Χρησιμοποιήστε τη φόρμα δημιουργίας παραπάνω για να υπολογίσετε μία." />
        </Card>
      ) : (
        <Card title={`💸 Μηνιαίες Εκκαθαρίσεις (${clearings.length})`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
            {clearings.map((c) => {
              const isPending = c.payout_status === 'pending';
              const isFormOpen = payingId === c.id;
              return (
                <div key={c.id} style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, background: isPending ? 'var(--bg, #fafafa)' : 'white' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtPeriod(c.period_year, c.period_month)}</div>
                    {statusBadge(c.payout_status)}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Χρεώσιμες</div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{c.billable_consultations ?? 0}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Έσοδα Ασθενών</div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{fmtEuro(c.total_patient_revenue_cents)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Μερίδιο Ιατρού</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy, #1e293b)' }}>{fmtEuro(c.doctor_share_cents)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Προμήθεια DocNow</div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{fmtEuro(c.docnow_commission_cents)}</div>
                      {c.vat_cents != null && (<div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>εκ των οποίων ΦΠΑ: {fmtEuro(c.vat_cents)}</div>)}
                    </div>
                  </div>
                  {(c.fallback_revenue_count > 0 || c.non_billable_count > 0) && (
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, padding: 8, background: '#FEF3C7', borderRadius: 6 }}>
                      {c.fallback_revenue_count > 0 && (<div>⚠️ {c.fallback_revenue_count} επισκέψεις χωρίς εγγραφή πληρωμής — χρησιμοποιήθηκε προεπιλεγμένη τιμή ιατρού</div>)}
                      {c.non_billable_count > 0 && (<div>ℹ️ {c.non_billable_count} μη χρεώσιμες επισκέψεις (doctor_no_show)</div>)}
                    </div>
                  )}
                  {c.payout_status === 'paid' && (c.payout_date || c.payout_reference) && (
                    <div style={{ fontSize: 13, padding: 10, background: '#F0FDF4', borderRadius: 6, border: '1px solid #BBF7D0', marginBottom: 12 }}>
                      <strong>Πληρωμή:</strong> {c.payout_date && fmtDate(c.payout_date)}
                      {c.payout_reference && (<> · αρ. αναφοράς: <code style={{ fontFamily: 'monospace' }}>{c.payout_reference}</code></>)}
                    </div>
                  )}
                  {isPending && !isFormOpen && (
                    <button onClick={() => openPayForm(c)} style={{ background: 'var(--crimson, #C0185A)', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      💰 Καταχώρηση Πληρωμής
                    </button>
                  )}
                  {isPending && isFormOpen && (
                    <div style={{ padding: 14, background: 'white', border: '1px solid var(--border)', borderRadius: 8 }}>
                      <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 13 }}>Καταχώρηση τραπεζικής πληρωμής</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 12 }}>Ημερομηνία *</label>
                          <DateInput value={payoutDate} max={new Date().toISOString().slice(0, 10)} onChange={e => setPayoutDate(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 12 }}>Αρ. αναφοράς τράπεζας *</label>
                          <input type="text" value={payoutReference} onChange={e => setPayoutReference(e.target.value)} placeholder="π.χ. SWIFT/BIC ID ή reference τραπέζης" style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={closePayForm} disabled={submitting} style={{ background: 'transparent', color: 'var(--slate, #475569)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 14px', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13 }}>Ακύρωση</button>
                        <button onClick={() => submitMarkPaid(c.id)} disabled={submitting} style={{ background: 'var(--crimson, #C0185A)', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: submitting ? 'wait' : 'pointer', fontWeight: 600, fontSize: 13, opacity: submitting ? 0.6 : 1 }}>
                          {submitting ? 'Καταχώρηση...' : '✅ Επιβεβαίωση'}
                        </button>
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>
                    Δημιουργήθηκε: {fmtDate(c.generated_at)}
                    {c.finalized_at && <> · Οριστικοποιήθηκε: {fmtDate(c.finalized_at)}</>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
